import {
  ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, Input, OnDestroy, OnInit, Output,
} from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { Subscription } from 'rxjs';
import { knxCompositeName, knxLocationName, knxName } from '../shared/knx-names';
import { uiLocale } from '../shared/chart.utils';

/**
 * GET /api/admin/knx/valves — the learned valve ↔ room mapping per floor block (KnxValveMapping.view;
 * field names read tolerantly, everything but `ga` is optional):
 *
 *   { updatedAt?, hours?, thresholds?: { minHours?, minMargin?, minScore?, supportSamples?, supportHours?,
 *                                        supportEpisodes?, episodeGapMinutes?, supportEvents? }, lastError?,
 *     blocks: [{ floor, floors?, actuator?, group?, why? (string | string[]), ambiguous?, hours?,
 *                rooms:  [{ deviceId, name?, floor?, room?, tempC?, setpointC?, valve? }],
 *                valves: [{ ga, actuator?, channel? (0-based), pwm?, name? (ETS book), valuePct?, ts?,
 *                           roomDeviceId?, roomName?, status?, learnedRoomDeviceId?, override? ({action, roomDeviceId}),
 *                           verified? ({actuator, channel ('A'…'F'), istGa, source} — only while the status is
 *                                      'verified'), verifiedRoomDeviceId? (the actuator table's room, also under an
 *                                      admin choice), verifiedIgnored? ('admin'),
 *                           bookRoomDeviceId?, bookIgnored? ('admin' | 'data' | 'verified'), score?, supported?, margin?, stableHours?,
 *                           candidates?: [{ deviceId, name?, score, levelScore?, dynScore?, supported?,
 *                                           hi? / mid? / lo?: {n, mean, episodes}, events?, up?, flat? }] }] }],
 *     rooms?: [every KNX room, as in a block],
 *     verifiedTable?: { source?, readAt?, entries?, applied?, agreed? (an admin chose the table's room),
 *                       skipped?: [{ ga, roomDeviceId?, roomName?, actuator?, channel?, istGa?, valveActuator?,
 *                                    reason? ('room' | 'valve' | 'actuator' | 'admin') }] } }
 *
 * Status precedence: admin choice ('confirmed') > read from the actuator ('verified') > ETS book ('book') >
 * learned ('likely' / 'candidate'); verified valves and their rooms are left out of the learning.
 *
 * POST /api/admin/knx/valves {ga, roomDeviceId|null, action: 'confirm'|'set'|'clear'} stores an admin
 * decision in the backend's mapping file (nothing is sent to the KNX bus); 'clear' on a verified valve
 * returns it to the actuator's assignment.
 */
type ValveStatus = 'confirmed' | 'verified' | 'likely' | 'candidate' | 'book';
type ValveAction = 'confirm' | 'set' | 'clear';

interface RawRoom {
  deviceId?: string; roomDeviceId?: string; id?: string;
  name?: string | null; floor?: string | null; room?: string | null;
  tempC?: number | null; setpointC?: number | null; valve?: string | null;
}
interface RawBand { n?: number; mean?: number | null; episodes?: number; }
/** The actuator table's entry for a valve: channel = the actuator's object block (A…F), istGa = the GA its Ist input listens to. */
interface RawVerified { actuator?: string | null; channel?: string | number | null; istGa?: string | null; source?: string | null; }
interface RawCandidate {
  roomDeviceId?: string; deviceId?: string; room?: string; name?: string | null;
  score?: number; levelScore?: number; dynScore?: number; supported?: boolean; hours?: number;
  hi?: RawBand; mid?: RawBand; lo?: RawBand; events?: number; up?: number; flat?: number;
}
interface RawValve {
  ga?: string; series?: string; actuator?: string | null; device?: string; src?: string; channel?: number | null; pwm?: boolean;
  valuePct?: number | null; pct?: number | null; value?: number | null; current?: number | null;
  ts?: number | null; lastTs?: number; updatedAt?: number;
  roomDeviceId?: string | null; assignedRoom?: string | null; room?: string | null; roomName?: string | null;
  learnedRoomDeviceId?: string | null; learnedRoomName?: string | null;
  status?: string | null; override?: boolean | { action?: string; roomDeviceId?: string | null } | null; source?: string;
  bookName?: string | null; name?: string | null; margin?: number | null; stableHours?: number | null;
  bookRoomDeviceId?: string | null; bookIgnored?: string | null; supported?: boolean | null;
  verified?: RawVerified | null; verifiedRoomDeviceId?: string | null; verifiedIgnored?: string | null;
  candidates?: RawCandidate[] | Record<string, number>; ranked?: RawCandidate[];
}
interface RawBlock {
  key?: string; floor?: string; floors?: string[]; actuator?: string; device?: string; group?: string; middle?: string;
  reason?: string | string[]; reasons?: string[]; why?: string | string[];
  ambiguous?: boolean; fallback?: boolean;
  hours?: number | null; dataHours?: number | null; margin?: number | null;
  rooms?: RawRoom[]; valves?: RawValve[];
}
interface RawValves {
  updatedAt?: number | null; hours?: number | null; lastError?: string | null;
  thresholds?: {
    minHours?: number; minMargin?: number; likelyHours?: number; likelyMargin?: number; minScore?: number;
    supportSamples?: number; supportHours?: number; supportEpisodes?: number; episodeGapMinutes?: number; supportEvents?: number;
  };
  blocks?: RawBlock[]; floors?: RawBlock[];
  /** Every KNX room (all floors): "Raum wählen" also offers the other floors' rooms. */
  rooms?: RawRoom[];
  /** The actuator table at a glance: how many entries are applied, how many an admin set the same way, the others with their reason. */
  verifiedTable?: {
    source?: string | null; readAt?: string | null; entries?: number; applied?: number; agreed?: number;
    skipped?: { ga?: string; roomDeviceId?: string | null; roomName?: string | null; actuator?: string | null;
                channel?: string | null; istGa?: string | null; valveActuator?: string | null; reason?: string | null }[];
  } | null;
}

/** One room of a block (view model). */
interface VRoom {
  id:       string;
  label:    string;
  ist:      string | null;
  soll:     string | null;
  /** Ist − Soll with sign as on the room cards, e.g. "−1,2°"; `need` = more than 0.5° below target (the valve should open). */
  delta:    string | null;
  need:     boolean;
  /** GA of the valve assigned to this room (any status), null: none. */
  valveGa:  string | null;
}
interface VCandidate { id: string; label: string; score: string; bar: number; assigned: boolean; title: string; }
interface VValve {
  ga:        string;
  actuator:  string;
  channel:   string;
  pwm:       boolean;
  pct:       number | null;
  pctText:   string;
  ago:       string;
  agoFull:   string;
  roomId:    string | null;
  roomLabel: string;
  /** The learned room when an admin choice overrides it with another one ("gelernt: Flur"). */
  learnedLabel: string;
  /** "Abstand +31 · stabil 26 h" (learned pairs), plus "zu wenig Belege" while the evidence is too thin for "vermutet". */
  facts:     string;
  status:    ValveStatus | null;
  override:  boolean;
  /** "Ist 7/1/12": the GA the actuator channel takes the room's actual temperature from, else ''. */
  istGa:     string;
  /** Tooltip of the actuator column for a valve from the actuator table: what it means and its source. */
  verifiedTitle: string;
  bookName:  string;
  /** Why the ETS book's room for this valve is not used ("ETS-Buch: Küche – widerspricht den Messdaten"), else ''. */
  bookNote:  string;
  /** The actuator table names another room, overruled by an admin choice ("Aktor-Tabelle nennt E1 WC – …"), else ''. */
  tableNote: string;
  cands:     VCandidate[];
}
interface VBlock {
  key:       string;
  floor:     string;
  /** Floor in the UI language ("E3" stays, "Außen" → "Outside"). */
  floorLabel: string;
  actuator:  string;
  group:     string;
  reasons:   string[];
  ambiguous: boolean;
  facts:     string;
  rooms:     VRoom[];
  valves:    VValve[];
  /** Rooms of the block without any valve (E5: six rooms, five valves). */
  unmatched: VRoom[];
  /** The other floors' rooms for "Raum wählen" (a floor block derived wrongly), floor + room. */
  others:    { id: string; label: string }[];
}

const POLL_MS = 10_000;
const NONE = '__none__';
const STATUSES: ValveStatus[] = ['confirmed', 'verified', 'likely', 'candidate', 'book'];

/**
 * Admin → KNX → Ventile: the heating actuators' valve positions (main group 16) and the valve ↔ room
 * mapping per floor block (read from the actuators where known, learned for the rest), with confirm /
 * set / reset. Polls every 10 s while shown (the component only exists while its tab is open) and the
 * browser tab is visible.
 */
@Component({
  selector: 'app-admin-knx-valves',
  templateUrl: './admin-knx-valves.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  styles: [`
    :host { display: flex; flex-direction: column; flex: 1 1 auto; min-height: 0; }
    .kv-scroll { flex: 1 1 auto; min-height: 0; overflow: auto; -webkit-overflow-scrolling: touch; }
    .kv-hint { font-size: .7rem; color: var(--bs-secondary-color); line-height: 1.35; }
    .kv-hint summary { cursor: pointer; color: var(--bs-link-color); width: fit-content; }
    .kv-test { font-size: .72rem; line-height: 1.35; border-left: 3px solid var(--bs-info-border-subtle); background: var(--bs-info-bg-subtle);
               color: var(--bs-info-text-emphasis); padding: .35rem .55rem; border-radius: .3rem; }
    .kv-block { border: 1px solid var(--bs-border-color); border-radius: .5rem; overflow: hidden; }
    .kv-block + .kv-block { margin-top: .75rem; }
    .kv-head { background: var(--bs-tertiary-bg); padding: .4rem .6rem; font-size: .78rem; }
    .kv-head-sub { font-size: .66rem; color: var(--bs-secondary-color); overflow-wrap: anywhere; }
    .kv-reasons { margin: .15rem 0 0; padding-left: 1rem; font-size: .66rem; color: var(--bs-secondary-color); overflow-wrap: anywhere; }
    .kv-rooms { display: flex; flex-wrap: wrap; gap: .3rem; padding: .4rem .6rem; border-top: 1px solid var(--bs-border-color-translucent); }
    .kv-room { font-size: .66rem; line-height: 1.25; padding: .15rem .4rem; border-radius: .3rem; background: var(--bs-secondary-bg); min-width: 0; }
    .kv-room.need { box-shadow: inset 0 0 0 1px var(--bs-primary-border-subtle); }
    .kv-room.none { background: transparent; border: 1px dashed var(--bs-border-color); color: var(--bs-secondary-color); }
    .kv-row { display: grid; grid-template-columns: 4.4rem 6.6rem 6.6rem minmax(8rem, 1fr) minmax(9rem, 1.4fr) 13rem;
              gap: .2rem .6rem; align-items: center; padding: .4rem .6rem; border-top: 1px solid var(--bs-border-color-translucent); font-size: .74rem; }
    .kv-row.kv-th { font-size: .64rem; font-weight: 600; color: var(--bs-secondary-color); padding-top: .25rem; padding-bottom: .25rem; }
    .kv-sub { font-size: .64rem; color: var(--bs-secondary-color); line-height: 1.2; overflow-wrap: anywhere; }
    .kv-pct { font-variant-numeric: tabular-nums; font-weight: 600; white-space: nowrap; }
    .kv-gauge { height: 5px; border-radius: 3px; background: var(--bs-secondary-bg); overflow: hidden; margin-top: .15rem; }
    .kv-gauge > span { display: block; height: 100%; background: #0ca678; transition: width .4s; }
    .kv-cands { display: flex; flex-direction: column; gap: .1rem; min-width: 0; }
    .kv-cand { display: grid; grid-template-columns: minmax(0, 1fr) 2.6rem 2.4rem; gap: .35rem; align-items: center; font-size: .68rem; }
    .kv-cand.on { font-weight: 600; }
    .kv-cand .kv-gauge { margin: 0; height: 4px; }
    .kv-cand .kv-gauge > span { background: var(--bs-secondary-color); }
    .kv-cand.on .kv-gauge > span { background: var(--bs-primary); }
    .kv-score { font-variant-numeric: tabular-nums; text-align: right; white-space: nowrap; }
    .kv-acts { display: flex; flex-wrap: wrap; gap: .25rem; align-items: center; }
    .kv-acts .btn, .kv-acts .form-select { font-size: .68rem; padding-top: .1rem; padding-bottom: .1rem; }
    .kv-acts .form-select { width: auto; max-width: 9.5rem; min-width: 0; }
    .kv-st { font-size: .6rem; font-weight: 600; margin-left: .3rem; vertical-align: .06rem; }
    .kv-st-confirmed { background: var(--bs-success-bg-subtle); color: var(--bs-success-text-emphasis); }
    .kv-st-verified  { background: transparent; color: var(--bs-success-text-emphasis); border: 1px solid var(--bs-success-border-subtle); }
    .kv-st-likely    { background: var(--bs-info-bg-subtle); color: var(--bs-info-text-emphasis); }
    .kv-st-candidate { background: transparent; color: var(--bs-warning-text-emphasis); border: 1px dashed var(--bs-warning-border-subtle); }
    .kv-st-book      { background: var(--bs-secondary-bg); color: var(--bs-body-color); }
    .kv-pwm { font-size: .56rem; font-weight: 600; background: var(--bs-warning-bg-subtle); color: var(--bs-warning-text-emphasis); margin-left: .3rem; }
    .kv-lbl { display: none; }
    .mono { font-family: var(--bs-font-monospace); }
    /* Below lg: each valve stacks — GA / actuator / now in one line, then room, candidates, actions */
    @media (max-width: 991.98px) {
      .kv-row { grid-template-columns: auto minmax(0, 1fr) minmax(5.5rem, auto);
                grid-template-areas: "ga act pct" "room room room" "cand cand cand" "acts acts acts"; }
      .kv-row.kv-th { display: none; }
      .kv-c-ga { grid-area: ga; } .kv-c-act { grid-area: act; } .kv-c-pct { grid-area: pct; text-align: right; }
      .kv-c-room { grid-area: room; } .kv-c-cand { grid-area: cand; } .kv-c-acts { grid-area: acts; }
      .kv-lbl { display: block; font-size: .6rem; color: var(--bs-secondary-color); text-transform: uppercase; letter-spacing: .04em; }
    }
    /* Phones: no text under 11 px; the room choice gets the full width, the two buttons share the row below it */
    @media (max-width: 575.98px) {
      .kv-head-sub, .kv-reasons, .kv-sub, .kv-lbl, .kv-st, .kv-pwm { font-size: .6875rem; }
      .kv-room, .kv-cand { font-size: .75rem; }
      .kv-hint summary { padding: .35rem 0; }
      .kv-acts { display: grid; grid-template-columns: 1fr 1fr; gap: .4rem; margin-top: .2rem; }
      .kv-acts .form-select { grid-column: 1 / -1; order: -1; width: 100%; max-width: none; min-height: var(--dash-touch-sm); font-size: 1rem; }
      .kv-acts .btn { min-height: var(--dash-touch-sm); font-size: .8125rem; }
      .kv-acts .spinner-border { grid-column: 1 / -1; justify-self: center; }
    }
  `],
})
export class AdminKnxValvesComponent implements OnInit, OnDestroy {
  /** Auth headers of the admin session (X-Session-Token), supplied by the dashboard. */
  @Input() authHeaders: () => Record<string, string> = () => ({});
  /** A request came back 401: the dashboard drops the session and shows the password prompt. */
  @Output() unauthorized = new EventEmitter<void>();

  blocks: VBlock[] = [];
  loaded = false;
  error: string | null = null;
  /** Last successful read (client clock) and its "14:32:05". */
  fetchedAt: number | null = null;
  fetchedTime = '';
  thresholdText = '';
  /** "Aus den Aktoren gelesen (Tabellen vom 14.10.2026): 20 von 20 Ventilen fest zugeordnet.", its source as tooltip. */
  tableText = '';
  tableTitle = '';
  /** Table entries not applied, each with its reason ("16/1/3 → E1 WC (Aktor 1.1.17, Kanal E): …"). */
  tableSkips: string[] = [];
  /** The backend's last learning / persistence problem. */
  backendError = '';
  /** GA → a POST is running for it (its buttons are disabled meanwhile). */
  busy: Record<string, boolean> = {};
  /** GA → error of its last POST. */
  postError: Record<string, string> = {};
  readonly none = NONE;

  private raw: RawValves | null = null;
  private timer: ReturnType<typeof setTimeout> | null = null;
  private gen = 0;
  private destroyed = false;
  private subs: Subscription[] = [];
  private readonly onVisibility = () => this.restart();

  constructor(private readonly translate: TranslateService, private readonly cdr: ChangeDetectorRef) {}

  ngOnInit(): void {
    this.subs.push(this.translate.onLangChange.subscribe(() => { if (this.raw) this.build(this.raw); this.cdr.markForCheck(); }));
    document.addEventListener('visibilitychange', this.onVisibility);
    this.restart();
  }

  ngOnDestroy(): void {
    this.destroyed = true;
    this.stop();
    document.removeEventListener('visibilitychange', this.onVisibility);
    this.subs.forEach(s => s.unsubscribe());
  }

  trackBlock = (_: number, b: VBlock) => b.key;
  trackValve = (_: number, v: VValve) => v.ga;
  trackRoom  = (_: number, r: VRoom) => r.id;
  trackCand  = (_: number, c: VCandidate) => c.id;

  // ── Polling (10 s while shown and the browser tab is visible) ─────────────────
  refresh(): void { this.restart(); }

  private restart(): void {
    this.stop();
    const gen = ++this.gen;
    if (this.destroyed || document.visibilityState === 'hidden') return;
    const run = (): void => {
      this.fetch().finally(() => {
        if (gen !== this.gen || this.destroyed) return;
        this.timer = setTimeout(run, POLL_MS);
      });
    };
    run();
  }

  private stop(): void {
    if (this.timer) clearTimeout(this.timer);
    this.timer = null;
  }

  private async request<T>(url: string, init: RequestInit = {}): Promise<{ data: T | null; error: string | null }> {
    try {
      const r = await fetch(url, { ...init, headers: { ...this.authHeaders(), ...((init.headers as Record<string, string> | undefined) ?? {}) } });
      if (r.status === 401) { this.unauthorized.emit(); return { data: null, error: 'HTTP 401' }; }
      if (!r.ok) {
        let msg = `HTTP ${r.status}`;
        try {
          const body = await r.text();
          try { const j = JSON.parse(body); if (j?.error) msg += ` – ${j.error}`; } catch { if (body && body.length < 200) msg += ` – ${body}`; }
        } catch { /* no body */ }
        return { data: null, error: msg };
      }
      const text = await r.text();
      return { data: (text ? JSON.parse(text) : null) as T, error: null };
    } catch (e: any) {
      return { data: null, error: e?.message || String(e) };
    }
  }

  private async fetch(): Promise<void> {
    const { data, error } = await this.request<RawValves | RawBlock[]>('/api/admin/knx/valves');
    if (this.destroyed) return;
    this.loaded = true;
    this.error = error;
    if (data) this.apply(data);
    this.cdr.markForCheck();
  }

  private apply(data: RawValves | RawBlock[]): void {
    this.raw = Array.isArray(data) ? { blocks: data } : data;
    this.fetchedAt = Date.now();
    this.build(this.raw);
  }

  // ── Admin decisions ─────────────────────────────────────────────────────────
  confirm(v: VValve): void {
    if (v.roomId) this.post(v.ga, v.roomId, 'confirm');
  }

  /** "Raum wählen …": the select posts right away and falls back to its placeholder. */
  pick(v: VValve, sel: HTMLSelectElement): void {
    const value = sel.value;
    sel.value = '';
    if (!value) return;
    this.post(v.ga, value === NONE ? null : value, 'set');
  }

  reset(v: VValve): void {
    this.post(v.ga, null, 'clear');
  }

  private async post(ga: string, roomDeviceId: string | null, action: ValveAction): Promise<void> {
    if (this.busy[ga]) return;
    this.busy = { ...this.busy, [ga]: true };
    const { [ga]: _drop, ...rest } = this.postError;
    this.postError = rest;
    this.cdr.markForCheck();
    const { data, error } = await this.request<RawValves | RawBlock[] | Record<string, unknown>>('/api/admin/knx/valves', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ ga, roomDeviceId, action }),
    });
    if (this.destroyed) return;
    const { [ga]: _done, ...still } = this.busy;
    this.busy = still;
    if (error) this.postError = { ...this.postError, [ga]: error };
    // The answer may carry the new mapping; otherwise read it again (and restart the 10 s cadence).
    const d = data as RawValves | RawBlock[] | null;
    if (d && (Array.isArray(d) || Array.isArray((d as RawValves).blocks) || Array.isArray((d as RawValves).floors))) {
      this.apply(d);
      this.cdr.markForCheck();
    } else {
      this.restart();
    }
    this.cdr.markForCheck();
  }

  // ── View model ──────────────────────────────────────────────────────────────
  private get lang(): string { return this.translate.currentLang || this.translate.defaultLang || 'en'; }
  private get locale(): string { return uiLocale(); }

  private num(v: number, digits: number): string {
    return v.toLocaleString(this.locale, { minimumFractionDigits: digits, maximumFractionDigits: digits });
  }

  private t(key: string, params?: Record<string, unknown>): string {
    return this.translate.instant(key, params);
  }

  private ago(ts: number): string {
    const s = Math.max(0, Math.round((Date.now() - ts) / 1000));
    const v = s < 60 ? `${s}s` : s < 3600 ? `${Math.floor(s / 60)}min` : s < 86400 ? `${Math.floor(s / 3600)}h` : `${Math.floor(s / 86400)}d`;
    return this.t('UPDATED_AGO', { time: v });
  }

  private static str(v: unknown): string | null {
    return typeof v === 'string' && v.trim() ? v.trim() : null;
  }

  private static numOrNull(v: unknown): number | null {
    return typeof v === 'number' && Number.isFinite(v) ? v : null;
  }

  private static status(v: unknown): ValveStatus | null {
    return typeof v === 'string' && (STATUSES as string[]).includes(v) ? v as ValveStatus : null;
  }

  private static gaCompare(a: string, b: string): number {
    const pa = a.split('/').map(Number), pb = b.split('/').map(Number);
    for (let i = 0; i < Math.max(pa.length, pb.length); i++) {
      const d = (pa[i] ?? 0) - (pb[i] ?? 0);
      if (d) return d;
    }
    return 0;
  }

  private build(raw: RawValves): void {
    const S = AdminKnxValvesComponent;
    const th = raw.thresholds ?? {};
    const minHours  = S.numOrNull(th.minHours ?? th.likelyHours);
    const minMargin = S.numOrNull(th.minMargin ?? th.likelyMargin);
    const minScore  = S.numOrNull(th.minScore);
    const supHours  = S.numOrNull(th.supportHours);
    const supEp     = S.numOrNull(th.supportEpisodes);
    const supEvents = S.numOrNull(th.supportEvents);
    const n0 = (v: number | null): string => v != null ? this.num(v, 0) : '–';
    this.thresholdText = minHours != null || minMargin != null
      ? this.t('ADMIN_KNX_VALVES_THRESHOLDS', {
          hours: n0(minHours), margin: minMargin != null ? this.fmtScore(minMargin) : '–',
          score: minScore != null ? this.fmtScore(minScore) : '–',
          supportHours: n0(supHours), episodes: n0(supEp), events: n0(supEvents),
        })
      : '';
    this.fetchedTime = this.fetchedAt ? new Date(this.fetchedAt).toLocaleTimeString(this.locale) : '';
    this.backendError = S.str(raw.lastError) ?? '';
    // Every room with floor + room: the other floors in "Raum wählen", names of rooms outside a block.
    const all: { id: string; label: string }[] = [];
    for (const r of Array.isArray(raw.rooms) ? raw.rooms : []) {
      const id = S.str(r?.deviceId ?? r?.roomDeviceId ?? r?.id);
      if (id) all.push({ id, label: this.fullLabel(r, id) });
    }
    const list = Array.isArray(raw.blocks) ? raw.blocks : Array.isArray(raw.floors) ? raw.floors : [];
    this.blocks = list.map((b, i) => this.toBlock(b, i, all));
    this.buildTable(raw.verifiedTable, all);
  }

  /** The actuator table's summary line and the entries that do not apply (they fall back to book / learning). */
  private buildTable(vt: RawValves['verifiedTable'], all: { id: string; label: string }[]): void {
    const S = AdminKnxValvesComponent;
    const entries = S.numOrNull(vt?.entries), applied = S.numOrNull(vt?.applied), agreed = S.numOrNull(vt?.agreed);
    const readAt = S.str(vt?.readAt);
    const date = readAt && /^\d{4}-\d{2}-\d{2}$/.test(readAt)
      ? new Date(`${readAt}T12:00:00`).toLocaleDateString(this.locale) : readAt;
    // An entry an admin confirmed as it is shows as 'confirmed', not 'verified': counted apart, so the sum adds up.
    this.tableText = entries != null && applied != null
      ? this.t('ADMIN_KNX_VALVES_TABLE', { applied, entries, date: date ?? '–' })
        + (agreed ? ' ' + this.t('ADMIN_KNX_VALVES_TABLE_AGREED', { agreed }) : '') : '';
    this.tableTitle = S.str(vt?.source) ?? '';
    const allName = new Map(all.map(r => [r.id, r.label] as [string, string]));
    this.tableSkips = (Array.isArray(vt?.skipped) ? vt!.skipped : []).filter(x => S.str(x?.ga)).map(x => {
      const id = S.str(x.roomDeviceId), name = S.str(x.roomName);
      const reason = S.str(x.reason) ?? '';
      const why = reason === 'room' ? this.t('ADMIN_KNX_VALVES_TABLE_WHY_ROOM')
        : reason === 'valve' ? this.t('ADMIN_KNX_VALVES_TABLE_WHY_VALVE')
        : reason === 'actuator'
          ? this.t('ADMIN_KNX_VALVES_TABLE_WHY_ACTUATOR', { sender: S.str(x.valveActuator) ?? this.t('ADMIN_KNX_VALVES_TABLE_UNKNOWN') })
        : reason === 'admin' ? this.t('ADMIN_KNX_VALVES_TABLE_WHY_ADMIN') : reason;
      return this.t('ADMIN_KNX_VALVES_TABLE_SKIP', {
        ga: S.str(x.ga), room: (id && allName.get(id)) ?? (name ? knxCompositeName(name, this.lang) : id ?? '–'),
        actuator: S.str(x.actuator) ?? '–', channel: S.str(x.channel) ?? '–', why,
      });
    });
  }

  /** "E4 Flur" / "E4 Hallway": floor and room (the room name alone is ambiguous across floors). */
  private fullLabel(r: RawRoom, id: string): string {
    return r.floor && r.room ? knxLocationName(r.floor, r.room, this.lang) : r.name ? knxCompositeName(r.name, this.lang) : id;
  }

  private toBlock(b: RawBlock, index: number, all: { id: string; label: string }[]): VBlock {
    const S = AdminKnxValvesComponent;
    const floor = S.str(b.floor) ?? S.str(b.key) ?? '';
    const floors = Array.isArray(b.floors) ? b.floors.map(f => S.str(f)).filter((f): f is string => !!f) : [];
    const ambiguous = !!(b.ambiguous ?? b.fallback);
    // Several floors in one block ("E2+E3", the fallback over all floors): "Flur" alone would be ambiguous.
    const withFloor = floors.length > 1 || ambiguous;
    const rawRooms = Array.isArray(b.rooms) ? b.rooms : [];
    const rawValves = (Array.isArray(b.valves) ? b.valves : []).filter(v => S.str(v?.ga));
    const roomName = new Map<string, string>();
    const rooms: VRoom[] = [];
    const ownValve = new Map<string, string>();
    for (const r of rawRooms) {
      const id = S.str(r?.deviceId ?? r?.roomDeviceId ?? r?.id);
      if (!id) continue;
      const own = S.str(r.valve);
      if (own) ownValve.set(id, own);
      const label = withFloor ? this.fullLabel(r, id)
        : r.room ? knxName(r.room, this.lang, 'room') : r.name ? knxCompositeName(r.name, this.lang) : id;
      roomName.set(id, label);
      const ist = S.numOrNull(r.tempC), soll = S.numOrNull(r.setpointC);
      const d = ist != null && soll != null ? ist - soll : null;
      rooms.push({
        id, label,
        ist:  ist != null ? `${this.num(ist, 1)}°` : null,
        soll: soll != null ? `${this.num(soll, 1)}°` : null,
        delta: d != null ? `${d >= 0.05 ? '+' : d <= -0.05 ? '−' : '±'}${this.num(Math.abs(d), 1)}°` : null,
        need: d != null && d < -0.5,
        valveGa: null,
      });
    }
    // Rooms outside the block's list (a candidate of an ambiguous block): their name from the valve entries.
    for (const v of rawValves) {
      const extra: [unknown, unknown][] = [[v.roomDeviceId, v.roomName], [v.learnedRoomDeviceId, v.learnedRoomName]];
      if (Array.isArray(v.candidates)) for (const c of v.candidates) extra.push([c?.deviceId ?? c?.roomDeviceId, c?.name]);
      for (const [id, name] of extra) {
        const i = S.str(id), n = S.str(name);
        if (i && n && !roomName.has(i)) roomName.set(i, knxCompositeName(n, this.lang));
      }
    }
    const allName = new Map(all.map(r => [r.id, r.label] as [string, string]));
    const label = (id: string | null): string => id ? roomName.get(id) ?? allName.get(id) ?? id : '';
    const inBlock = new Set(rooms.map(r => r.id));

    const valves = rawValves.map(v => this.toValve(v, label)).sort((a, b) => S.gaCompare(a.ga, b.ga));
    for (const r of rooms) r.valveGa = ownValve.get(r.id) ?? null;
    for (const v of valves) {
      const r = v.roomId ? rooms.find(x => x.id === v.roomId) : undefined;
      if (r && !r.valveGa) r.valveGa = v.ga;
    }

    // The backend's display text ("16/1/0…4"); a bare middle group ("16/1") gets the "/…" itself.
    const rawGroup = S.str(b.group ?? b.middle) ?? (valves[0] ? valves[0].ga.split('/').slice(0, 2).join('/') : '');
    const group = rawGroup.split('/').length === 2 ? `${rawGroup}/…` : rawGroup;
    const actuator = S.str(b.actuator ?? b.device) ?? (valves.find(v => v.actuator)?.actuator ?? '');
    const why = b.reasons ?? b.reason ?? b.why;
    const reasons = (Array.isArray(why) ? why : why ? [why] : []).map(x => String(x)).filter(Boolean);
    const hours = S.numOrNull(b.hours ?? b.dataHours);
    const margin = S.numOrNull(b.margin);
    const verified = valves.filter(v => v.status === 'verified').length;
    const facts = [
      this.t('ADMIN_KNX_VALVES_COUNTS', { valves: valves.length, rooms: rooms.length }),
      verified ? this.t('ADMIN_KNX_VALVES_VERIFIED_COUNT', { n: verified }) : '',
      hours != null ? this.t('ADMIN_KNX_VALVES_HOURS', { hours: this.num(hours, hours < 10 ? 1 : 0) }) : '',
      margin != null ? this.t('ADMIN_KNX_VALVES_MARGIN', { margin: this.fmtScore(margin) }) : '',
    ].filter(Boolean).join(' · ');
    return {
      key: `${floor}|${group}|${index}`,
      floor, floorLabel: (floors.length ? floors : floor ? [floor] : []).map(f => knxName(f, this.lang, 'floor')).join(' / '),
      actuator, group, reasons,
      ambiguous,
      facts, rooms, valves,
      unmatched: rooms.filter(r => !r.valveGa),
      others: all.filter(r => !inBlock.has(r.id)),
    };
  }

  private toValve(v: RawValve, label: (id: string | null) => string): VValve {
    const S = AdminKnxValvesComponent;
    const ga = S.str(v.ga)!;
    const pct = S.numOrNull(v.valuePct ?? v.pct ?? v.current ?? v.value);
    const ts = S.numOrNull(v.ts ?? v.lastTs ?? v.updatedAt);
    const roomId = S.str(v.roomDeviceId ?? v.assignedRoom ?? v.room);
    const sub = Number(ga.split('/')[2]);
    const channel = S.numOrNull(v.channel) ?? (Number.isFinite(sub) ? sub : null);

    // Candidates: a ranked list, or a {deviceId: score} map.
    let rawC: RawCandidate[] = [];
    const c = v.candidates ?? v.ranked;
    if (Array.isArray(c)) rawC = c;
    else if (c && typeof c === 'object') rawC = Object.entries(c).map(([roomDeviceId, score]) => ({ roomDeviceId, score: Number(score) }));
    const cands = rawC
      .map(x => ({ id: S.str(x?.roomDeviceId ?? x?.deviceId ?? x?.room), score: S.numOrNull(x?.score), title: this.evidence(x) }))
      .filter((x): x is { id: string; score: number | null; title: string } => !!x.id)
      .sort((a, b) => (b.score ?? -Infinity) - (a.score ?? -Infinity));
    const top = Math.max(0, ...cands.map(x => x.score ?? 0));
    const learned = S.str(v.learnedRoomDeviceId);
    const margin = S.numOrNull(v.margin), stable = S.numOrNull(v.stableHours);
    const status = S.status(v.status);
    const bookRoom = S.str(v.bookRoomDeviceId), ignored = S.str(v.bookIgnored);
    // The actuator table's channel is its object block letter ("E"); a number would be the block index.
    const ver = v.verified && typeof v.verified === 'object' ? v.verified : null;
    const verChannel = typeof ver?.channel === 'number' && ver.channel >= 0 && ver.channel < 26
      ? String.fromCharCode(65 + ver.channel) : S.str(ver?.channel);
    const istGa = S.str(ver?.istGa);
    const source = S.str(ver?.source);
    const tableRoom = S.str(v.verifiedRoomDeviceId);

    return {
      ga,
      actuator: S.str(v.actuator ?? v.device ?? v.src) ?? '',
      channel: verChannel ? this.t('ADMIN_KNX_VALVES_CHANNEL', { n: verChannel })
        : channel != null ? this.t('ADMIN_KNX_VALVES_CHANNEL', { n: channel + 1 }) : '',
      pwm: !!v.pwm,
      pct,
      pctText: pct != null ? `${this.num(pct, 0)} %` : '–',
      ago: ts ? this.ago(ts) : '',
      agoFull: ts ? new Date(ts).toLocaleString(this.locale) : '',
      roomId,
      roomLabel: roomId ? label(roomId) : '',
      learnedLabel: learned && learned !== roomId ? label(learned) : '',
      facts: [
        margin != null ? this.t('ADMIN_KNX_VALVES_PAIR_MARGIN', { margin: this.fmtScore(margin) }) : '',
        stable != null ? this.t('ADMIN_KNX_VALVES_PAIR_STABLE', { hours: this.num(stable, stable < 10 ? 1 : 0) }) : '',
        status === 'candidate' && roomId && v.supported === false ? this.t('ADMIN_KNX_VALVES_PAIR_THIN') : '',
      ].filter(Boolean).join(' · '),
      status,
      override: !!v.override || v.source === 'admin',
      istGa: istGa ? this.t('ADMIN_KNX_VALVES_IST_GA', { ga: istGa }) : '',
      verifiedTitle: ver ? [this.t('ADMIN_KNX_VALVES_VERIFIED_HINT'), source].filter(Boolean).join('\n') : '',
      bookName: S.str(v.bookName ?? v.name) ?? '',
      bookNote: this.bookNote(bookRoom, ignored, roomId, status === 'verified', label),
      tableNote: tableRoom && tableRoom !== roomId && S.str(v.verifiedIgnored) === 'admin'
        ? this.t('ADMIN_KNX_VALVES_TABLE_OVERRULED', { room: label(tableRoom) }) : '',
      cands: cands.map(x => ({
        id: x.id,
        label: label(x.id),
        score: x.score != null ? this.fmtScore(x.score) : '–',
        bar: top > 0 && x.score != null && x.score > 0 ? Math.round(x.score / top * 100) : 0,
        assigned: x.id === roomId,
        title: x.title,
      })),
    };
  }

  /**
   * Why the ETS book's room for this valve is not used, else ''. 'verified': the actuator tables give
   * this valve another room, or the book's room to another valve (agreeing with them: no note).
   */
  private bookNote(bookRoom: string | null, ignored: string | null, roomId: string | null, verified: boolean,
                   label: (id: string | null) => string): string {
    if (!bookRoom) return '';
    const key = ignored === 'data' ? 'ADMIN_KNX_VALVES_BOOK_IGNORED_DATA'
      : ignored === 'admin' ? 'ADMIN_KNX_VALVES_BOOK_IGNORED_ADMIN'
      : ignored === 'verified' && bookRoom !== roomId
        ? (verified ? 'ADMIN_KNX_VALVES_BOOK_IGNORED_VERIFIED' : 'ADMIN_KNX_VALVES_BOOK_IGNORED_VERIFIED_ROOM')
      : null;
    return key ? this.t(key, { room: label(bookRoom) }) : '';
  }

  /**
   * Candidate tooltip, as the backend scores: the valve's mean per band of the room (demand / near
   * target / none, with samples and episodes), the score's parts (level + ½ × dynamic), the room's
   * response to openings and whether the evidence suffices for "vermutet".
   */
  private evidence(c: RawCandidate): string {
    const S = AdminKnxValvesComponent;
    const band = (b: RawBand | undefined): string => {
      const mean = S.numOrNull(b?.mean);
      return this.t('ADMIN_KNX_VALVES_EV_BAND', {
        mean: mean != null ? `${this.num(mean, 0)} %` : '–', n: S.numOrNull(b?.n) ?? 0, ep: S.numOrNull(b?.episodes) ?? 0,
      });
    };
    const parts: string[] = [];
    if (c.hi || c.mid || c.lo) parts.push(this.t('ADMIN_KNX_VALVES_EV_BANDS', { hi: band(c.hi), mid: band(c.mid), lo: band(c.lo) }));
    const score = S.numOrNull(c.score), level = S.numOrNull(c.levelScore), dyn = S.numOrNull(c.dynScore);
    if (score != null && level != null && dyn != null) {
      parts.push(this.t('ADMIN_KNX_VALVES_EV_SCORE', { score: this.fmtScore(score), level: this.fmtScore(level), dyn: this.fmtScore(dyn) }));
    }
    const events = S.numOrNull(c.events);
    if (events) parts.push(this.t('ADMIN_KNX_VALVES_EV_RISES', { up: S.numOrNull(c.up) ?? 0, flat: S.numOrNull(c.flat) ?? 0, events }));
    if (typeof c.supported === 'boolean') parts.push(this.t(c.supported ? 'ADMIN_KNX_VALVES_EV_SUPPORT_OK' : 'ADMIN_KNX_VALVES_EV_SUPPORT_THIN'));
    const hours = S.numOrNull(c.hours);
    if (hours != null) parts.push(this.t('ADMIN_KNX_VALVES_HOURS', { hours: this.num(hours, hours < 10 ? 1 : 0) }));
    return parts.join('\n');
  }

  /** Scores are differences of mean valve % (−100 … 100): whole numbers, small ones with decimals. */
  private fmtScore(s: number): string {
    const digits = Math.abs(s) >= 10 || s === 0 ? 0 : Math.abs(s) >= 1 ? 1 : 2;
    const v = this.num(Math.abs(s), digits);
    return s > 0 ? `+${v}` : s < 0 ? `−${v}` : v;
  }
}
